import React, { useState } from 'react';
import { Button, Input, Dropdown, Empty, Flex } from 'antd';
import {
  MenuOutlined,
  MoreOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { AskMeAnythingProvider } from '../../../../features/askMeAnything/context/AskMeAnythingProvider';
import { Questions } from '../../../../features/askMeAnything/questions';
import { ChatItem } from '../../../../services/types';
import { Chat } from '../../types';
import {
  ChatPanelsContainer,
  Panel,
  ChatListHeader,
  ChatList,
  StyledChatItem,
  FloatingHeader,
  ChatItemHeader,
} from './styles';
import { AskMeAnythingChat3 } from '../../../../features/shared/components/AskMeAnything/AskMeAnythingChat3';

interface ChatPanelsProps {
  chats: Chat[];
  showList: boolean;
  setShowList: (show: boolean) => void;
  selectedChat: string;
  setSelectedChat: (chatId: string) => void;
  createNewChat: () => void;
  askFunction: (
    chatMessages: ChatItem[],
    from?: Date,
    to?: Date,
  ) => Promise<ReadableStream<Uint8Array> | null>;
  location: string;
  deleteChat: (chatId: string) => void;
  renameChat: (chatId: string, newName: string) => void;
}

export const ChatPanels: React.FC<ChatPanelsProps> = ({
  chats,
  showList,
  setShowList,
  selectedChat,
  setSelectedChat,
  createNewChat,
  askFunction,
  location,
  deleteChat,
  renameChat,
}) => {
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingChatName, setEditingChatName] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <ChatPanelsContainer showList={showList}>
      <Panel>
        <ChatListHeader>
          <h2>Chats</h2>
          <Button type="primary" onClick={createNewChat}>
            <PlusOutlined /> New Chat
          </Button>
        </ChatListHeader>

        <ChatList>
          {chats.length === 0 && (
            <Flex align="center" justify="center" style={{ height: '100%' }} vertical>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No chats yet. Create your first chat to get started!"
              />
            </Flex>
          )}
          {chats.map(chat => (
            <StyledChatItem
              key={chat.chatId}
              onClick={() => {
                if (isDropdownOpen) {
                  return;
                }

                if (!editingChatId) {
                  setSelectedChat(chat.chatId);
                  setShowList(false);
                }
              }}
            >
              {editingChatId === chat.chatId ? (
                <Input
                  value={editingChatName}
                  onChange={e => setEditingChatName(e.target.value)}
                  onBlur={() => {
                    if (editingChatName.trim()) {
                      renameChat(chat.chatId, editingChatName.trim());
                    }
                    setEditingChatId(null);
                  }}
                  onPressEnter={() => {
                    if (editingChatName.trim()) {
                      renameChat(chat.chatId, editingChatName.trim());
                    }
                    setEditingChatId(null);
                  }}
                  onClick={e => e.stopPropagation()}
                  autoFocus
                />
              ) : (
                <div>
                  <ChatItemHeader>
                    <h3>{chat.chat}</h3>
                  </ChatItemHeader>
                  <p>{new Date(chat.createdAt).toLocaleDateString()}</p>
                </div>
              )}

              <Dropdown
                trigger={['click']}
                onOpenChange={setIsDropdownOpen}
                menu={{
                  items: [
                    {
                      label: 'Rename',
                      key: 'rename-chat',
                      icon: <EditOutlined />,
                      onClick: () => {
                        setEditingChatId(chat.chatId);
                        setEditingChatName(chat.chat);
                      },
                    },
                    {
                      label: 'Delete',
                      icon: <DeleteOutlined />,
                      danger: true,
                      key: 'delete-chat',
                      onClick: () => deleteChat(chat.chatId),
                    },
                  ],
                }}
              >
                <Button
                  type="text"
                  icon={<MoreOutlined />}
                  size="small"
                  onClick={e => e.stopPropagation()}
                />
              </Dropdown>
            </StyledChatItem>
          ))}
        </ChatList>
      </Panel>

      <Panel>
        <FloatingHeader>
          <Button type="text" icon={<MenuOutlined />} onClick={() => setShowList(true)} />
          <h2>{chats.find(c => c.chatId === selectedChat)?.chat || 'New Chat'}</h2>
        </FloatingHeader>
        <AskMeAnythingProvider
          amaKey={selectedChat || `${location}-default`}
          suggestedQuestions={{
            data: Questions.iaDashboard,
            category: 'all',
            displayDropdown: false,
          }}
        >
          <AskMeAnythingChat3
            fullMessageBubble={true}
            location={location}
            askFunction={(chatMessages: ChatItem[], from?: Date, to?: Date) =>
              askFunction(chatMessages, from, to)
            }
            styles={{ height: 'calc(100% - 103px - 24px)', padding: '0 16px' }}
          />
        </AskMeAnythingProvider>
      </Panel>
    </ChatPanelsContainer>
  );
};
